import type {
  ListWebhookEventsResponse,
  MarkWebhookProcessedResponse,
  WebhookEventRecord,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

const BASE = '/v1/ops/webhook-events';

export interface WebhookListFilters {
  readonly provider?: string;
  readonly processed?: 'true' | 'false';
  readonly since?: string;
  readonly limit?: number;
}

function toQueryString(f: WebhookListFilters): string {
  const params = new URLSearchParams();
  if (f.provider !== undefined && f.provider.length > 0) params.set('provider', f.provider);
  if (f.processed !== undefined) params.set('processed', f.processed);
  if (f.since !== undefined && f.since.length > 0) params.set('since', f.since);
  if (f.limit !== undefined) params.set('limit', String(f.limit));
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

export async function listWebhookEvents(
  filters: WebhookListFilters,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<WebhookEventRecord[]> {
  const response = await opsApiRequest<ListWebhookEventsResponse>(
    `${BASE}${toQueryString(filters)}`,
    { signal },
    fetchImpl,
  );
  return response.events;
}

export async function markWebhookProcessed(
  id: string,
  fetchImpl: typeof fetch,
): Promise<WebhookEventRecord> {
  const response = await opsApiRequest<MarkWebhookProcessedResponse>(
    `${BASE}/${encodeURIComponent(id)}/mark-processed`,
    { method: 'POST' },
    fetchImpl,
  );
  return response.event;
}

export function webhookEventsQueryKey(
  filters: WebhookListFilters,
): readonly ['webhook-events', WebhookListFilters] {
  return ['webhook-events', filters] as const;
}

export function useWebhookEvents(
  filters: WebhookListFilters,
): UseQueryResult<WebhookEventRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: webhookEventsQueryKey(filters),
    queryFn: ({ signal }) => listWebhookEvents(filters, fetchImpl, signal),
  });
}

export function useMarkWebhookProcessed(): UseMutationResult<
  WebhookEventRecord,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => markWebhookProcessed(id, fetchImpl),
    onSuccess: () => {
      // Any list with any filter set could now be stale — blanket
      // invalidate the webhook-events key root.
      void qc.invalidateQueries({ queryKey: ['webhook-events'] });
    },
  });
}
